/**
 * aethereum agent add [name] — bring another agent/machine into the CURRENT room.
 *
 * Reads the active room (token + roomId) from this project's config, POSTs to
 * /api/rooms/[id]/agents to mint a sibling token, and prints the exact command to
 * run on the other machine. This is the "second seat" path: one room, many agents.
 *
 * Fails soft: offline / API-down / missing-room prints a clear message, exits 1.
 */

import { homedir } from "node:os";
import { AccountClient, OfflineError } from "../hive/account-client.js";
import { machineHandle } from "../hive/machine-handle.js";
import { resolveProject } from "../hive/config-store.js";

export interface AgentOptions {
  url?: string;
  /** Override the room token (else read from the active project config). */
  token?: string;
  /** Override the room id (else read from the active project config). */
  roomId?: string;
  home?: string;
  cwd?: string;
  env?: Record<string, string | undefined>;
  fetchImpl?: typeof fetch;
}

const DEFAULT_BASE = "https://www.aethereum.dev";

export async function runAgent(args: string[], options: AgentOptions = {}): Promise<void> {
  const sub = args[0];
  if (sub !== "add") {
    console.error("Usage: aethereum agent add [name]");
    process.exit(1);
    return;
  }

  const home = options.home ?? homedir();
  const cwd = options.cwd ?? process.cwd();
  const env = options.env ?? (process.env as Record<string, string | undefined>);
  const project = resolveProject(cwd, home);

  const token = options.token ?? env["AETHEREUM_SYNC_TOKEN"] ?? project?.token;
  const roomId = options.roomId ?? project?.roomId;
  const base = (options.url ?? env["AETHEREUM_SYNC_API_URL"] ?? project?.url ?? DEFAULT_BASE)
    .replace(/\/$/, "")
    .replace(/^https:\/\/aethereum\.dev(?=$|\/)/, "https://www.aethereum.dev");

  if (!token) {
    console.error("No room wired here. Run 'aethereum init' first, then 'aethereum agent add'.");
    process.exit(1);
    return;
  }
  if (!roomId) {
    console.error(
      "This room's id is unknown locally (older config). Run 'aethereum reconnect' to recover it (keeps your existing room), then 'aethereum agent add'.",
    );
    process.exit(1);
    return;
  }

  const name = args
    .slice(1)
    .filter((a) => !a.startsWith("--"))
    .join(" ")
    .trim();

  const client = new AccountClient(base, options.fetchImpl ?? fetch);
  try {
    const { token: siblingToken, handle } = await client.addAgent(token, roomId, name || machineHandle());
    console.log(`\n  [ok] added agent${handle ? ` "${handle}"` : ""} to this room.\n`);
    console.log("  Run this on the other machine (or in another project) to join the SAME room:");
    console.log(`    npx -y aethereum init --token ${siblingToken}\n`);
  } catch (err) {
    if (err instanceof OfflineError) {
      console.error("Could not reach Aethereum (offline?). Try again when you are online.");
    } else {
      console.error(err instanceof Error ? err.message : String(err));
    }
    process.exit(1);
  }
}
